import type {usePanorama} from '@wix/yoshi-flow-editor'

export const CHECKOUT_VISIBILITY_MESSAGE = 'Checkout visibility stage'
export const CHECKOUT_VISIBILITY_QUERY_PARAM = 'checkoutVisibility'

const STORAGE_KEY = 'wix-events-checkout-visibility-v1'
const ROUTE_CONTEXT_VERSION = 1

export enum CheckoutFlowType {
  Legacy = 'legacy',
}

export enum CheckoutStage {
  Attempt = 'checkout_attempt',
  Eligibility = 'eligibility',
  Reservation = 'reservation',
  CheckoutForm = 'checkout_form',
  OrderCreation = 'order_creation',
  OrderUpdate = 'order_update',
  PaymentUi = 'payment_ui',
  PaymentSubmission = 'payment_submission',
  PaymentAuthorization = 'payment_authorization',
  OrderConfirmation = 'order_confirmation',
  ThankYou = 'thank_you',
  TicketsReady = 'tickets_ready',
}

export enum CheckoutStageOutcome {
  Started = 'started',
  Success = 'success',
  BusinessFailure = 'business_failure',
  TechnicalFailure = 'technical_failure',
  Cancelled = 'cancelled',
  Pending = 'pending',
}

export enum CheckoutPaymentPath {
  Free = 'free',
  Offline = 'offline',
  Online = 'online',
  Express = 'express',
  Unselected = 'unselected',
}

export interface CheckoutStageContext {
  reason?: string
  errorCode?: string
  requestId?: string
  paymentStatus?: string
  isFree?: boolean
  isExpress?: boolean
  recovered?: boolean
}

export interface CheckoutFailure {
  outcome: CheckoutStageOutcome.BusinessFailure | CheckoutStageOutcome.TechnicalFailure
  context: CheckoutStageContext
}

interface StoredCheckoutAttempt {
  id: string
  flowType: CheckoutFlowType
  startedAt: number
  completedAt?: number
  stageStartedAt: Partial<Record<CheckoutStage, number>>
  stageOutcomes: Partial<Record<CheckoutStage, CheckoutStageOutcome>>
  attemptOutcome?: CheckoutStageOutcome
  paymentPath?: CheckoutPaymentPath
}

interface CheckoutVisibilityRouteContext {
  version: number
  attempt: StoredCheckoutAttempt
}

interface StorageLike {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
}

interface CreateCheckoutVisibilityParams {
  panoramaClient: ReturnType<typeof usePanorama>
  storage?: StorageLike
  now?: () => number
  createId?: () => string
}

export const createCheckoutVisibility = ({
  panoramaClient,
  storage = getSessionStorage(),
  now = Date.now,
  createId = createAttemptId,
}: CreateCheckoutVisibilityParams) => {
  let memoryAttempt: StoredCheckoutAttempt | undefined

  const readAttempt = (): StoredCheckoutAttempt | undefined => {
    try {
      const value = storage?.getItem(STORAGE_KEY)
      const attempt = value ? JSON.parse(value) : undefined

      if (isStoredCheckoutAttempt(attempt)) {
        memoryAttempt = normalizeAttempt(attempt)
      }
    } catch {
      // Fall back to memory when storage is unavailable.
    }

    return memoryAttempt
  }

  const writeAttempt = (attempt: StoredCheckoutAttempt) => {
    memoryAttempt = attempt

    try {
      storage?.setItem(STORAGE_KEY, JSON.stringify(attempt))
    } catch {
      // Visibility must never interrupt checkout when storage is unavailable.
    }
  }

  const log = (
    attempt: StoredCheckoutAttempt,
    stage: CheckoutStage,
    outcome: CheckoutStageOutcome,
    context: CheckoutStageContext = {},
  ) => {
    const timestamp = now()
    const stageStartedAt = attempt.stageStartedAt[stage]

    try {
      panoramaClient?.logger?.()?.info(CHECKOUT_VISIBILITY_MESSAGE, {
        checkout_attempt_id: attempt.id,
        flow_type: attempt.flowType,
        payment_path: attempt.paymentPath,
        stage,
        outcome,
        timestamp,
        duration_ms:
          outcome !== CheckoutStageOutcome.Started && stageStartedAt !== undefined
            ? Math.max(0, timestamp - stageStartedAt)
            : undefined,
        ...context,
      })
    } catch {
      // Visibility must never interrupt checkout when logging is unavailable.
    }
  }

  const startAttempt = (flowType: CheckoutFlowType, context: CheckoutStageContext = {}) => {
    const attempt: StoredCheckoutAttempt = {
      id: createId(),
      flowType,
      startedAt: now(),
      stageStartedAt: {
        [CheckoutStage.Attempt]: now(),
      },
      stageOutcomes: {},
    }

    writeAttempt(attempt)
    log(attempt, CheckoutStage.Attempt, CheckoutStageOutcome.Started, context)

    return attempt.id
  }

  const recoverAttempt = (replaceCompleted = false) => {
    const attempt = readAttempt()

    if (attempt && (!attempt.completedAt || !replaceCompleted)) {
      return attempt.id
    }

    const recoveredAttempt: StoredCheckoutAttempt = {
      id: createId(),
      flowType: CheckoutFlowType.Legacy,
      startedAt: now(),
      stageStartedAt: {
        [CheckoutStage.Attempt]: now(),
      },
      stageOutcomes: {},
    }

    writeAttempt(recoveredAttempt)
    log(recoveredAttempt, CheckoutStage.Attempt, CheckoutStageOutcome.Started, {recovered: true})

    return recoveredAttempt.id
  }

  const restoreAttempt = (serializedContext?: string) => {
    const restoredAttempt = parseRouteContext(serializedContext)

    if (!restoredAttempt) {
      return false
    }

    const currentAttempt = readAttempt()
    const attempt =
      currentAttempt?.id === restoredAttempt.id
        ? {
            ...restoredAttempt,
            ...currentAttempt,
            stageStartedAt: {...restoredAttempt.stageStartedAt, ...currentAttempt.stageStartedAt},
            stageOutcomes: {...restoredAttempt.stageOutcomes, ...currentAttempt.stageOutcomes},
            paymentPath: currentAttempt.paymentPath ?? restoredAttempt.paymentPath,
          }
        : restoredAttempt

    writeAttempt(attempt)
    return true
  }

  const getRouteContext = () => {
    const attempt = readAttempt()

    return attempt
      ? JSON.stringify({
          version: ROUTE_CONTEXT_VERSION,
          attempt,
        } satisfies CheckoutVisibilityRouteContext)
      : undefined
  }

  const setPaymentPath = (paymentPath: CheckoutPaymentPath) => {
    const attempt = readAttempt()

    if (attempt) {
      attempt.paymentPath = paymentPath
      writeAttempt(attempt)
    }
  }

  const getPaymentPath = () => readAttempt()?.paymentPath

  const reportStage = (stage: CheckoutStage, outcome: CheckoutStageOutcome, context: CheckoutStageContext = {}) => {
    const attempt = readAttempt()

    if (!attempt || (attempt.completedAt && !POST_COMPLETION_STAGES.has(stage))) {
      return
    }

    if (outcome === CheckoutStageOutcome.Started) {
      attempt.stageStartedAt[stage] = now()
      writeAttempt(attempt)
      return
    }

    attempt.stageOutcomes[stage] = outcome
    writeAttempt(attempt)
    log(attempt, stage, outcome, context)
  }

  const reportStageOnce = (stage: CheckoutStage, outcome: CheckoutStageOutcome, context: CheckoutStageContext = {}) => {
    if (readAttempt()?.stageOutcomes[stage] !== outcome) {
      reportStage(stage, outcome, context)
    }
  }

  const finishAttempt = (outcome: CheckoutStageOutcome, context: CheckoutStageContext = {}) => {
    const attempt = readAttempt()

    if (!attempt) {
      return
    }

    if (attempt.attemptOutcome === outcome) {
      return
    }

    attempt.attemptOutcome = outcome
    if (TERMINAL_OUTCOMES.has(outcome)) {
      attempt.completedAt = now()
    }

    writeAttempt(attempt)
    log(attempt, CheckoutStage.Attempt, outcome, context)
  }

  const getAttemptId = () => {
    const attempt = readAttempt()

    return attempt?.completedAt ? undefined : attempt?.id
  }

  return {
    startAttempt,
    recoverAttempt,
    restoreAttempt,
    reportStage,
    reportStageOnce,
    finishAttempt,
    getAttemptId,
    getRouteContext,
    setPaymentPath,
    getPaymentPath,
  }
}

export const appendCheckoutVisibilityToUrl = (url: string, routeContext?: string) => {
  if (!url || !routeContext) {
    return url
  }

  const hashIndex = url.indexOf('#')
  const hash = hashIndex >= 0 ? url.slice(hashIndex) : ''
  const urlWithoutHash = hashIndex >= 0 ? url.slice(0, hashIndex) : url
  const queryIndex = urlWithoutHash.indexOf('?')
  const path = queryIndex >= 0 ? urlWithoutHash.slice(0, queryIndex) : urlWithoutHash
  const searchParams = new URLSearchParams(queryIndex >= 0 ? urlWithoutHash.slice(queryIndex + 1) : '')
  searchParams.set(CHECKOUT_VISIBILITY_QUERY_PARAM, routeContext)

  return `${path}?${searchParams.toString()}${hash}`
}

export const getCheckoutFailure = (error: any): CheckoutFailure => {
  const status = Number(error?.response?.status)
  const applicationErrorCode = getApplicationErrorCode(error)
  const isTechnicalStatus = Number.isFinite(status) && (status === 429 || status >= 500)

  return {
    outcome:
      applicationErrorCode && !isTechnicalStatus
        ? CheckoutStageOutcome.BusinessFailure
        : CheckoutStageOutcome.TechnicalFailure,
    context: getCheckoutFailureContext(error, applicationErrorCode),
  }
}

export const getCheckoutFailureContext = (
  error: any,
  applicationErrorCode = getApplicationErrorCode(error),
): CheckoutStageContext => ({
  reason: String(error?.name ?? 'request_failed'),
  errorCode: String(applicationErrorCode ?? error?.code ?? error?.response?.status ?? 'unknown'),
  requestId: error?.requestId ?? error?.response?.headers?.['x-wix-request-id'],
})

export const getPaymentFailureContext = (result: any): CheckoutStageContext => ({
  errorCode: String(result?.errorCode ?? 'unknown'),
  paymentStatus: String(result?.serverStatus ?? result?.status ?? result?.clientStatus ?? 'unknown'),
})

const getApplicationErrorCode = (error: any): unknown =>
  error?.response?.data?.details?.applicationError?.code ??
  error?.response?.data?.metadata?.error_key ??
  error?.response?.data?.metadata?.code ??
  error?.response?.data?.code

const POST_COMPLETION_STAGES = new Set<CheckoutStage>([
  CheckoutStage.OrderConfirmation,
  CheckoutStage.ThankYou,
  CheckoutStage.TicketsReady,
])

const TERMINAL_OUTCOMES = new Set<CheckoutStageOutcome>([CheckoutStageOutcome.Success, CheckoutStageOutcome.Cancelled])

const getSessionStorage = (): StorageLike | undefined => {
  try {
    return typeof window === 'undefined' ? undefined : window.sessionStorage
  } catch {
    return undefined
  }
}

const createAttemptId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }

  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
}

const parseRouteContext = (serializedContext?: string): StoredCheckoutAttempt | undefined => {
  if (!serializedContext) {
    return undefined
  }

  try {
    const context: CheckoutVisibilityRouteContext = JSON.parse(serializedContext)

    return context?.version === ROUTE_CONTEXT_VERSION && isStoredCheckoutAttempt(context.attempt)
      ? normalizeAttempt(context.attempt)
      : undefined
  } catch {
    return undefined
  }
}

const normalizeAttempt = (attempt: StoredCheckoutAttempt): StoredCheckoutAttempt => ({
  ...attempt,
  stageStartedAt: attempt.stageStartedAt ?? {},
  stageOutcomes: attempt.stageOutcomes ?? {},
})

const isStoredCheckoutAttempt = (attempt: any): attempt is StoredCheckoutAttempt =>
  typeof attempt?.id === 'string' &&
  Object.values(CheckoutFlowType).includes(attempt.flowType) &&
  typeof attempt.startedAt === 'number' &&
  attempt.stageStartedAt !== null &&
  typeof attempt.stageStartedAt === 'object' &&
  (attempt.stageOutcomes === undefined ||
    (attempt.stageOutcomes !== null && typeof attempt.stageOutcomes === 'object')) &&
  (attempt.paymentPath === undefined || Object.values(CheckoutPaymentPath).includes(attempt.paymentPath))
